import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

const BrandIcon = ({ size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#7C3AED"/>
    <rect x="6" y="9" width="20" height="17" rx="2" stroke="white" strokeWidth="1.5" fill="none"/>
    <rect x="6" y="9" width="20" height="5" rx="1.5" fill="rgba(255,255,255,0.15)"/>
    <line x1="11" y1="7" x2="11" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="21" y1="7" x2="21" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <rect x="8" y="16" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="14" y="16" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="8" y="21" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="14" y="21" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="20" y="16" width="4" height="3" rx="1" fill="#4ADE80"/>
    <rect x="20" y="21" width="4" height="3" rx="1" fill="#4ADE80"/>
  </svg>
);

export const Login = () => {
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, cacheSecurityError, retryCacheCleanup } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password);
      navigate(userData.role === 'SUPER_ADMIN' ? '/dashboard' : '/planner');
    } catch (err) {
      // login() rejects when clearAuthCaches fails. cacheSecurityError
      // is set in AuthContext; the blocking page below will render
      // instead of the form on the next render cycle.
      setError(err?.message || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await retryCacheCleanup();
      // On success, cacheSecurityError becomes null and the form
      // below re-renders.
    } catch (err) {
      // cacheSecurityError stays active. The blocking page stays.
    } finally {
      setRetrying(false);
    }
  };

  // Block the form entirely while a cache-security boundary has
  // failed. The user must explicitly retry the cleanup; we never
  // let them submit credentials while the previous identity's cache
  // state is uncertain.
  if (cacheSecurityError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-8">
            <BrandIcon size={64} />
            <div className="flex flex-col items-center mt-4">
              <span style={{ color: 'white', fontWeight: 600, fontSize: '32px', letterSpacing: '-0.5px', lineHeight: 1 }}>AdamSched</span>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', letterSpacing: '3px', fontSize: '13px', marginTop: '4px' }}>Health & Fitness</span>
            </div>
          </div>
          <div
            role="alert"
            aria-live="assertive"
            className="space-y-3 p-4 rounded-sm bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
            data-testid="login-cache-security-error"
          >
            <div className="flex items-center gap-2 font-medium">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Limpieza de caché fallida
            </div>
            <p>
              Por seguridad no podemos continuar hasta vaciar el caché
              del navegador. Pulsa "Reintentar limpieza" para volver a
              intentarlo; si falla de nuevo, prueba a recargar la
              página o usar otro navegador.
            </p>
            {cacheSecurityError.detail && (
              <p className="text-xs text-red-500/80 font-mono break-all">
                {cacheSecurityError.detail}
              </p>
            )}
            <Button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="w-full btn-primary h-10"
              data-testid="login-retry-cleanup"
            >
              {retrying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Reintentar limpieza'
              )}
            </Button>
          </div>
          <div className="mt-8 text-center text-xs text-zinc-600">
            AdamSched Health & Fitness v1.0 • Gestión de citas profesional
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <BrandIcon size={64} />
          <div className="flex flex-col items-center mt-4">
            <span style={{ color: 'white', fontWeight: 600, fontSize: '32px', letterSpacing: '-0.5px', lineHeight: 1 }}>AdamSched</span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', letterSpacing: '3px', fontSize: '13px', marginTop: '4px' }}>Health & Fitness</span>
          </div>
          <p style={{ color: '#6b7280', fontSize: '11px', letterSpacing: '2.5px', marginTop: '14px', textTransform: 'uppercase' }}>Llena tu agenda. Gana más.</p>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="input-tactical"
              required
              data-testid="login-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input-tactical pr-10"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-zinc-500 hover:text-zinc-300"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full btn-primary h-10"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t('auth.login')
            )}
          </Button>

          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-sm text-gray-500 hover:text-gray-700 underline mt-2 block mx-auto"
            data-testid="login-forgot-password"
          >
            {t('auth.forgotPassword')}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-zinc-600">
          AdamSched Health & Fitness v1.0 • Gestión de citas profesional
        </div>
      </div>
    </div>
  );
};
